import { useState } from "react";
import axios from "axios";
import { Loader2, ShieldAlert } from "lucide-react";
import { usePageTitle } from "../lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { id: "new", label: "New", active: "bg-ice text-deep" },
  { id: "contacted", label: "Contacted", active: "bg-ink text-white" },
  { id: "quoted", label: "Quoted", active: "bg-ink text-white" },
];

const statusStyle = (s) => STATUSES.find((x) => x.id === s)?.active || "bg-mist text-slate";

const Admin = () => {
  usePageTitle("Admin");
  const [key, setKey] = useState(sessionStorage.getItem("stige-admin-key") || "");
  const [enquiries, setEnquiries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [updating, setUpdating] = useState(null);

  const setStatus = async (id, status) => {
    setUpdating(id);
    try {
      await axios.patch(`${API}/enquiries/${id}/status`, { status }, { headers: { "X-Admin-Key": key } });
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
    } catch {
      setError("Could not update status. Please reload and try again.");
    } finally {
      setUpdating(null);
    }
  };

  const load = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/enquiries`, { headers: { "X-Admin-Key": key } });
      setEnquiries(res.data);
      sessionStorage.setItem("stige-admin-key", key);
    } catch {
      setError("Invalid admin key or unable to load enquiries.");
      setEnquiries(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pt-nav min-h-screen bg-ice">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 xl:px-16 py-16">
        <h1 className="font-display text-4xl font-medium text-ink" data-testid="admin-headline">Enquiries</h1>
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-pine mt-3">Stige — Internal</p>

        <form onSubmit={load} className="flex flex-col sm:flex-row gap-4 mt-10 max-w-xl">
          <input
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Admin key"
            data-testid="admin-key-input"
            className="flex-1 bg-white border border-ink/25 focus:border-ink outline-none px-4 py-3 text-sm"
          />
          <button
            type="submit"
            disabled={loading || !key}
            data-testid="admin-load-button"
            className="inline-flex items-center justify-center gap-2 bg-ink text-white font-semibold px-6 py-3 rounded text-sm disabled:opacity-50 transition-colors hover:bg-deep"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Load Enquiries"}
          </button>
        </form>

        {error && (
          <div data-testid="admin-error" className="flex items-center gap-3 text-red-600 text-sm border border-red-200 bg-red-50 px-4 py-3 mt-6 max-w-xl">
            <ShieldAlert size={18} /> {error}
          </div>
        )}

        {enquiries && (
          <div className="mt-10 space-y-6" data-testid="admin-enquiries-list">
            <div className="flex flex-wrap items-center gap-4">
              <p className="font-mono text-xs tracking-[0.2em] uppercase text-slate">{enquiries.length} enquiries</p>
              <div className="flex gap-2">
                {STATUSES.map((s) => (
                  <span key={s.id} data-testid={`admin-count-${s.id}`} className={`font-mono text-[10px] tracking-[0.15em] uppercase px-3 py-1.5 ${s.active}`}>
                    {s.label}: {enquiries.filter((e) => (e.status || "new") === s.id).length}
                  </span>
                ))}
              </div>
            </div>
            {enquiries.length === 0 && <p className="text-slate text-sm">No enquiries yet.</p>}
            {enquiries.map((e) => (
              <div key={e.id} data-testid={`admin-enquiry-${e.id}`} className="bg-white border border-ice p-6 md:p-8">
                <div className="flex flex-wrap items-baseline justify-between gap-3 pb-4 border-b border-ice">
                  <div>
                    <p className="font-display text-xl font-medium text-ink">{e.company}</p>
                    <p className="text-sm text-slate">{e.name} · {e.country} · {e.email}{e.phone ? ` · ${e.phone}` : ""}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex flex-wrap items-center gap-2 justify-end">
                      <span className="inline-block bg-mist text-ink font-mono text-[11px] tracking-[0.15em] uppercase px-3 py-1.5">{e.product_category}</span>
                      <span data-testid={`admin-status-${e.id}`} className={`inline-block font-mono text-[11px] tracking-[0.15em] uppercase px-3 py-1.5 ${statusStyle(e.status || "new")}`}>
                        {e.status || "new"}
                      </span>
                    </div>
                    <p className="font-mono text-[10px] text-slate mt-2">{new Date(e.created_at).toLocaleString()}</p>
                  </div>
                </div>
                <p className="text-sm text-ink/75 leading-relaxed mt-4">{e.requirement}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
                  {[["Quantity", e.quantity], ["Destination", e.destination_country], ["Certifications", e.certifications], ["Packaging / PL", e.packaging]].map(([l, v]) => (
                    v ? (
                      <div key={l}>
                        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate">{l}</p>
                        <p className="text-sm text-ink/70 mt-1">{v}</p>
                      </div>
                    ) : null
                  ))}
                </div>
                {e.details && (
                  <p className="text-sm text-slate leading-relaxed mt-4 pt-4 border-t border-ice">{e.details}</p>
                )}
                <div className="flex flex-wrap items-center gap-2 mt-5 pt-4 border-t border-ice">
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate mr-2">Mark as</span>
                  {STATUSES.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setStatus(e.id, s.id)}
                      disabled={updating === e.id || (e.status || "new") === s.id}
                      data-testid={`admin-mark-${s.id}-${e.id}`}
                      className={`font-mono text-[11px] tracking-[0.15em] uppercase px-3 py-1.5 border transition-colors duration-300 disabled:opacity-40 disabled:pointer-events-none ${
                        (e.status || "new") === s.id
                          ? "border-transparent " + s.active
                          : "border-ink/25 text-ink/60 hover:border-ink hover:text-ink"
                      }`}
                    >
                      {updating === e.id ? "…" : s.label}
                    </button>
                  ))}
                </div>
                <p className="font-mono text-[10px] text-ink/30 mt-4">REF {e.id}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

export default Admin;
